/**
 * المرحلة 1.4 (P12, D01) — the visit note, written as SOAP.
 *
 * Four sections: what the patient says (S), what the doctor finds (O), the
 * assessment (A) and the plan (P). The doctor's own dictionary (the words
 * they wrote before, the same one the prescription editor reads) offers
 * itself under the assessment and the plan. The sections the institution's
 * `clinicalNote` policy requires are marked; under `free`, the visit keeps
 * today's free-text note and this says so.
 *
 * While the visit is open a save updates the draft in place; after it is
 * closed every save is a new version with its reason (useEncounter.write).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, Textarea } from '../../components/common';
import { encounterApi } from '../../api/endpoints';
import { cleanSoap, soapDirty, SOAP_SECTIONS } from '../../domain/encounter';
import { formatDateTime } from '../../domain/locale';
import { useI18n } from '../../i18n/I18nProvider';

/** Which of the doctor's dictionaries a section draws on. */
const DICTIONARY = { a: 'diagnosis', p: 'notes' };

/** The line the caret is on, for the suggestions. */
const lastLine = (value) => String(value || '').split('\n').pop().trim();

export function SoapEditor({ encounter, write, autocomplete = null, compact = false }) {
  const { t, lang } = useI18n();
  const policy = (encounter.policy && encounter.policy.clinicalNote) || { format: 'both', required: [] };
  const saved = useMemo(() => cleanSoap(encounter.soap || {}), [encounter.soap]);
  const [draft, setDraft] = useState(saved);
  const [focus, setFocus] = useState(null);
  const savedKey = `${encounter.id}|${encounter.soap && encounter.soap.at}|${(encounter.soapVersions || []).length}`;
  const lastKey = useRef(savedKey);
  const dirty = soapDirty(saved, draft);

  // A fresh copy from the server replaces the draft only when nothing is typed over it.
  useEffect(() => {
    if (lastKey.current === savedKey) return;
    lastKey.current = savedKey;
    if (!dirty) setDraft(saved);
  }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (policy.format === 'free') {
    return (
      <div className="enc-banner" data-testid="soap-free">
        <Icon name="info" size={16} />
        <span>{t('soap.freeOnly')}</span>
      </div>
    );
  }

  const canWrite = Boolean(encounter.can && encounter.can.write);
  const required = policy.required || [];
  const versions = encounter.soapVersions || [];
  const complaint = encounter.booking && encounter.booking.complaint;

  const save = () =>
    write((reason) => encounterApi.saveSoap(encounter.id, { ...cleanSoap(draft), ...(reason ? { reason } : {}) }), {
      ok: t('soap.saved'),
      what: t('visitPrint.what.soap'),
    });

  const pick = (key, word) => {
    setDraft((d) => {
      const lines = String(d[key] || '').split('\n');
      lines[lines.length - 1] = word;
      return { ...d, [key]: lines.join('\n') };
    });
  };

  if (!canWrite) {
    const filled = SOAP_SECTIONS.filter((k) => String(saved[k] || '').trim());
    if (!filled.length) return <p className="enc-empty">{t('soap.empty')}</p>;
    return (
      <div className={`soap-grid${compact ? ' is-compact' : ''}`} data-testid="soap-read">
        {filled.map((k) => (
          <div className="soap-sec" data-sec={k} key={k}>
            <div className="soap-label">
              <span className="soap-letter">{k.toUpperCase()}</span>
              {t(`soap.sections.${k}`)}
            </div>
            <p className="soap-read">{saved[k]}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="soap-editor">
      {complaint && !String(draft.s || '').trim() && (
        <div className="enc-banner mb-3">
          <Icon name="message-square" size={16} />
          <span className="flex-1">{t('soap.complaint', { text: complaint })}</span>
          <button type="button" className="enc-chip is-accent" onClick={() => setDraft((d) => ({ ...d, s: complaint }))}>
            {t('soap.useComplaint')}
          </button>
        </div>
      )}
      <div className={`soap-grid${compact ? ' is-compact' : ''}`}>
        {SOAP_SECTIONS.map((k) => {
          const field = DICTIONARY[k];
          const suggestions = field && autocomplete && focus === k ? autocomplete.suggest(field, lastLine(draft[k]), 6).filter((w) => w !== lastLine(draft[k])) : [];
          return (
            <div className="soap-sec" data-sec={k} key={k}>
              <label className="soap-label" htmlFor={`soap-${encounter.id}-${k}`}>
                <span className="soap-letter">{k.toUpperCase()}</span>
                {t(`soap.sections.${k}`)}
                {required.includes(k) && <span className="soap-required">{t('soap.required')}</span>}
              </label>
              <Textarea
                id={`soap-${encounter.id}-${k}`}
                rows={compact ? 3 : 4}
                value={draft[k]}
                placeholder={t(`soap.placeholders.${k}`)}
                onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                onFocus={() => setFocus(k)}
                onBlur={() => setTimeout(() => setFocus((f) => (f === k ? null : f)), 150)}
                data-testid={`soap-${k}`}
              />
              {suggestions.length > 0 && (
                <div className="soap-sug" aria-label={t('soap.suggestions')}>
                  {suggestions.map((w) => (
                    <button key={w} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(k, w)}>
                      {w}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="soap-foot">
        <span>
          {dirty ? (
            <span className="soap-dirty">{t('soap.unsaved')}</span>
          ) : versions.length ? (
            t('soap.version', {
              n: versions[0].version || versions.length,
              by: versions[0].byName || '—',
              at: formatDateTime(versions[0].at, { lang }),
            })
          ) : (
            t('soap.notSaved')
          )}
        </span>
        <Button icon="save" onClick={save} disabled={!dirty} data-testid="soap-save">
          {encounter.status === 'open' ? t('soap.save') : t('soap.saveVersion')}
        </Button>
      </div>
    </div>
  );
}

export default SoapEditor;
